const ROOT_ID = 'slot-landscape-blocker';

/** Portrait phone — animated from landscape (90deg) to portrait (0deg). */
const ROTATE_ICON_SVG = `
  <svg class="slot-landscape-blocker__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" fill="none" aria-hidden="true">
    <rect x="16" y="8" width="16" height="32" rx="3.5" stroke="#F0D78C" stroke-width="2" />
    <rect
      x="18.5"
      y="11.5"
      width="11"
      height="24"
      rx="1.5"
      fill="#2A2018"
      stroke="#C9A227"
      stroke-width="1.25"
      opacity="0.9"
    />
    <rect x="20.5" y="9.5" width="7" height="1.5" rx="0.75" fill="#7A5530" />
    <circle cx="24" cy="38.5" r="1.25" fill="#C9A227" />
  </svg>
`;

/**
 * Full-screen rotate-to-portrait overlay — Egypt-themed modal panel (matches in-game modals).
 * @param {{
 *   mountEl?: HTMLElement | null,
 *   isActive: () => boolean,
 *   getTitle: () => string,
 *   getBody: () => string,
 * }} opts
 */
export function createLandscapeOrientationBlocker({
  mountEl = typeof document !== 'undefined' ? document.body : null,
  isActive,
  getTitle,
  getBody,
} = {}) {
  if (!mountEl || typeof document === 'undefined') {
    return { sync: () => {}, dispose: () => {} };
  }

  let root = document.getElementById(ROOT_ID);
  if (!root) {
    root = document.createElement('div');
    root.id = ROOT_ID;
    root.className = 'slot-landscape-blocker';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-live', 'polite');
    root.innerHTML = `
      <div class="slot-landscape-blocker__backdrop" aria-hidden="true"></div>
      <div class="slot-landscape-blocker__content">
        <div class="slot-landscape-blocker__panel" role="presentation">
          <div class="slot-landscape-blocker__panel-rim" aria-hidden="true"></div>
          <div class="slot-landscape-blocker__panel-accent slot-landscape-blocker__panel-accent--top" aria-hidden="true"></div>
          <div class="slot-landscape-blocker__panel-accent slot-landscape-blocker__panel-accent--bottom" aria-hidden="true"></div>
          <div class="slot-landscape-blocker__panel-inner">
            <img
              class="slot-landscape-blocker__logo"
              src="/assets/Logo0.png"
              alt=""
              decoding="async"
              draggable="false"
            />
            <div class="slot-landscape-blocker__icon-badge" aria-hidden="true">
              ${ROTATE_ICON_SVG}
            </div>
            <h1 class="slot-landscape-blocker__title"></h1>
            <p class="slot-landscape-blocker__body"></p>
          </div>
        </div>
      </div>
    `;
    mountEl.appendChild(root);
  }

  const titleEl = root.querySelector('.slot-landscape-blocker__title');
  const bodyEl = root.querySelector('.slot-landscape-blocker__body');

  const refreshCopy = () => {
    if (titleEl) titleEl.textContent = getTitle?.() ?? '';
    if (bodyEl) bodyEl.textContent = getBody?.() ?? '';
    const label = [getTitle?.(), getBody?.()].filter(Boolean).join('. ');
    if (label) root.setAttribute('aria-label', label);
  };

  const sync = () => {
    refreshCopy();
    const show = !!isActive?.();
    root.classList.toggle('slot-landscape-blocker--visible', show);
    root.toggleAttribute('inert', !show);
    if (show) {
      window.getSelection?.()?.removeAllRanges?.();
      titleEl?.blur?.();
      bodyEl?.blur?.();
    }
  };

  refreshCopy();

  return {
    sync,
    refreshCopy,
    dispose() {
      root?.remove();
    },
  };
}
